import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { Container, DisplayHeading, DotList, Eyebrow, Postmark } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW, INK_RULE } from '@/travo/theme/constants';

interface BoxStampProps {
  kicker: string;
  country: string;
  city: string;
  border: string;
  color: string;
  tilt: string;
  radius?: string;
}

/** Rectangular entry/arrival stamp. */
const BoxStamp = ({ kicker, country, city, border, color, tilt, radius = '0' }: BoxStampProps) => (
  <Flex
    direction="column"
    align="center"
    gap="2px"
    border={border}
    color={color}
    borderRadius={radius}
    px="18px"
    py="12px"
    transform={`rotate(${tilt})`}
  >
    <Text fontFamily="mono" fontSize="11px" letterSpacing="0.16em">
      {kicker}
    </Text>
    <Text className="display" fontFamily="heading" fontWeight="900" fontSize="28px" lineHeight="1.2">
      {country}
    </Text>
    <Text fontFamily="mono" fontSize="12px" letterSpacing="0.12em">
      {city}
    </Text>
  </Flex>
);

interface PassportRuleProps {
  left: string;
  right: string;
  /** Which edge of the page the rule sits on. */
  edge: 'top' | 'bottom';
}

/** Printed line at the top or bottom of the passport page. */
const PassportRule = ({ left, right, edge }: PassportRuleProps) => (
  <Flex
    justify="space-between"
    gap="12px"
    fontFamily="mono"
    fontSize="12px"
    letterSpacing="0.14em"
    {...(edge === 'top' ? { borderBottom: INK_RULE.line, pb: '12px' } : { borderTop: INK_RULE.line, pt: '12px', color: 'muted' })}
  >
    <Text>{left}</Text>
    <Text color="muted">{right}</Text>
  </Flex>
);

/** Four sample stamps: two round postmarks, two box stamps. */
const SampleStamps = () => {
  const { passport } = useCopy();
  const roundSize = { base: '130px', md: '170px' };

  return (
    <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="20px" alignItems="center" justifyItems="center" minH="360px">
      <Postmark ring="JAPAN · KYOTO · ARRIVED · · · ·" big="JP" title={passport.japanAlt} w={roundSize} h={roundSize} color="rust" transform="rotate(-10deg)" />
      <BoxStamp kicker="ENTRY" country={passport.thailand} city="BANGKOK" border="3px double" color="stamp" tilt="7deg" radius="10px" />
      <BoxStamp kicker="✈ ARRIVAL" country={passport.indonesia} city="BALI" border="3px solid" color="ink" tilt="-4deg" />
      <Postmark ring="TAIWAN · TAIPEI · HOME BASE · ·" big="TW" dashed title={passport.taiwanAlt} w={roundSize} h={roundSize} color="plum" transform="rotate(14deg)" />
    </Grid>
  );
};

/** Passport page mock framing the sample stamps. */
const PassportPage = () => {
  const { passport } = useCopy();

  return (
    <Flex
      direction="column"
      gap="20px"
      flex="1 1 460px"
      minW={0}
      bg="card"
      border="2px solid"
      borderColor="ink"
      boxShadow={HARD_SHADOW.card}
      p={{ base: '20px', md: '28px' }}
    >
      <PassportRule edge="top" left="PASSPORT · TRV · @travo" right="VISAS" />
      <SampleStamps />
      <PassportRule edge="bottom" left="PAGE 1 · STAMPS 2026" right={passport.sample} />
    </Flex>
  );
};

const PassportCopy = () => {
  const { passport } = useCopy();

  return (
    <Flex direction="column" gap="24px" flex="1 1 420px" minW={0}>
      <Eyebrow>{passport.eyebrow}</Eyebrow>
      <DisplayHeading fontSize={{ base: '36px', md: '54px' }}>{passport.title}</DisplayHeading>
      <Text color="inkSoft" maxW="30em">
        {passport.body}
      </Text>
      <Box>
        <DotList items={passport.points} dot="sun" />
      </Box>
    </Flex>
  );
};

/** Passport page mock with sample stamps, beside what the passport tracks. */
const Passport = () => (
  <Container as="section" py={{ base: '80px', md: '112px' }}>
    <Flex wrap="wrap" align="center" gap="64px">
      <PassportPage />
      <PassportCopy />
    </Flex>
  </Container>
);

export default Passport;
